<template>
  <div class="department-page">
    <div class="page-shell">
      <header class="page-toolbar">
        <div>
          <el-button type="text" icon="el-icon-arrow-left" @click="$router.push('/')">返回创建页</el-button>
          <p>{{ loading ? '正在读取签署任务…' : `${studentTitle} · 文件编号：${unionId || '—'}` }}</p>
        </div>
        <div class="toolbar-actions">
          <el-button :disabled="loading || Boolean(loadError)" @click="saveDraft">保存页面草稿</el-button>
          <el-button type="success" :disabled="loading || Boolean(loadError) || photosUploading" :loading="submitting" @click="submitDocuments">提交文件</el-button>
        </div>
      </header>

      <div v-if="loading" class="page-state" v-loading="loading">正在加载签署任务</div>
      <div v-else-if="loadError" class="page-state page-state-error">
        <h2>无法打开签署任务</h2>
        <p>{{ loadError }}</p>
        <el-button type="primary" @click="loadDepartmentDocument">重新加载</el-button>
      </div>
      <template v-else>
        <main
          v-if="selectedDocumentKeys.includes('health')"
          class="document-paper"
        >
          <basic-health-survey
            ref="healthSurvey"
            :form="health"
          />
        </main>

        <main
          v-if="selectedDocumentKeys.includes('profile')"
          class="document-paper"
        >
          <student-profile
            :form="profile"
            @photos-change="photosDirty = true"
            @photos-uploading="photosUploading = $event"
          />
        </main>

        <footer class="page-footer">
          <div>
            <strong>文件编号：{{ unionId }}</strong>
            <span>“保存页面草稿”仅保存在本机；“提交文件”会按此文件编号更新。</span>
          </div>
          <el-button type="success" :disabled="photosUploading" :loading="submitting" @click="submitDocuments">提交文件</el-button>
        </footer>
      </template>
    </div>
  </div>
</template>

<script>
import BasicHealthSurvey from "@/components/BasicHealthSurvey.vue";
import StudentProfile from "@/components/StudentProfile.vue";
import { GetDepartmentDocument, SubmitDepartmentDocument } from "@/api/home";
import {
  createHealthSurvey,
  createStudentProfile,
} from "@/utils/departmentDocuments";

const DOCUMENTS = [
  { key: "health", number: "01", label: "基本健康问卷", english: "Basic Health Survey" },
  { key: "profile", number: "02", label: "学生档案", english: "Student Profile" },
];

export default {
  name: "DepartmentDocuments",
  components: { BasicHealthSurvey, StudentProfile },
  data() {
    return {
      health: createHealthSurvey(),
      profile: createStudentProfile(),
      unionId: "",
      taskTitle: "",
      taskDocs: [],
      taskStatus: 0,
      loading: true,
      loadError: "",
      submitting: false,
      photosDirty: false,
      photosUploading: false,
    };
  },
  computed: {
    studentTitle() {
      return this.taskTitle || "未命名学生";
    },
    selectedDocuments() {
      return DOCUMENTS.filter(item => this.taskDocs.includes(item.key));
    },
    selectedDocumentKeys() {
      return this.selectedDocuments.map(item => item.key);
    },
    storageKey() {
      return `naae-sign:department-preview:${this.unionId}`;
    },
  },
  created() {
    this.loadDepartmentDocument();
  },
  methods: {
    normalizeDocs(docs) {
      const values = Array.isArray(docs) ? docs : String(docs || "").split(",");
      return ["health", "profile"].filter(item => values.includes(item));
    },
    restoreDraftIfNeeded(serverData) {
      if (Number(serverData.status) !== 0) return;
      try {
        const draft = JSON.parse(localStorage.getItem(this.storageKey));
        if (draft) {
          this.health = createHealthSurvey(draft.health);
          this.profile = createStudentProfile(draft.profile);
          this.photosDirty = Boolean(draft.photos_dirty);
        }
      } catch (error) {
        localStorage.removeItem(this.storageKey);
      }
    },
    async loadDepartmentDocument() {
      this.unionId = String(this.$route.query.union_id || "").trim();
      this.loadError = "";
      if (!this.unionId) {
        this.loading = false;
        this.loadError = "链接缺少 union_id，请返回首页重新创建签署任务。";
        return;
      }
      this.loading = true;
      try {
        const response = await GetDepartmentDocument(this.unionId);
        const data = response.data || {};
        const docs = this.normalizeDocs(data.docs);
        if (!data.union_id || docs.length === 0) throw new Error("签署任务数据不完整");
        if (Number(data.status) === 3) throw new Error("签署链接已作废");
        this.unionId = data.union_id;
        this.taskTitle = data.title || "未命名学生";
        this.taskDocs = docs;
        this.taskStatus = Number(data.status || 0);
        this.health = createHealthSurvey(data.health);
        this.profile = createStudentProfile(data.profile);
        this.photosDirty = false;
        this.restoreDraftIfNeeded(data);
      } catch (error) {
        this.loadError = this.errorMessage(error, "获取签署任务失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    async collectHealthSignatures() {
      if (!this.$refs.healthSurvey) return;
      const parentSignature = await this.$refs.healthSurvey.generateParentSignature();
      const studentSignature = await this.$refs.healthSurvey.generateStudentSignature();
      if (parentSignature) this.health.parent_signature = parentSignature;
      if (studentSignature) this.health.student_signature = studentSignature;
    },
    async saveDraft() {
      if (this.selectedDocumentKeys.includes("health")) await this.collectHealthSignatures();
      this.persistDraft(true);
    },
    persistDraft(showMessage) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          title: this.studentTitle,
          health: this.health,
          profile: this.profile,
          union_id: this.unionId,
          photos_dirty: this.photosDirty,
          saved_at: new Date().toISOString(),
        }));
        if (showMessage) this.$message.success("页面草稿已保存在当前浏览器");
        return true;
      } catch (error) {
        if (showMessage) this.$message.error("本地存储空间不足，照片较大时请直接提交");
        return false;
      }
    },
    validateHealth() {
      if (!this.health.gender) return "健康问卷：请选择性别";
      const requiredItems = [
        ["skin_problem", "1. 皮肤问题"],
        ["body_aches", "2. 肌肉酸痛"],
        ["diarrhea_vomiting", "3. 腹泻和/或呕吐"],
        ["eye_problems", "4. 眼疾"],
        ["allergies_status", "5. 过敏史"],
        ["organ_problem", "6. 内脏器官问题"],
        ["breathing_problem", "7. 呼吸或咳嗽问题"],
        ["blood_problem", "8. 血液问题"],
        ["fainting", "9. 昏厥"],
        ["injuries", "10. 伤痛/跌伤"],
        ["mental_illness", "11. 心理或精神疾病"],
        ["daily_medicines", "12. 日常服药"],
        ["other_problem", "13. 其他健康问题"],
      ];
      const hasPhysicalProblem = requiredItems.some(([field]) => this.health[field] === "yes");
      if (this.health.no_physical_illness && hasPhysicalProblem) return "健康问卷：已选择健康问题，不能同时勾选第14项";
      if (!this.health.no_physical_illness) {
        const missing = requiredItems.find(([field]) => !["yes", "no"].includes(this.health[field]));
        if (missing) return `健康问卷：请选择${missing[1]}`;
      }
      if (this.health.skin_problem === "yes" && !["yes", "no"].includes(this.health.skin_contagious)) return "健康问卷：请选择皮肤问题是否传染";
      if (this.health.blood_problem === "yes" && !["yes", "no"].includes(this.health.blood_contagious)) return "健康问卷：请选择血液问题是否传染";
      if (this.health.allergies_status === "yes" && !this.health.allergy_types.length && !this.health.allergy_other) return "健康问卷：请选择或填写过敏类型";
      if (this.health.organ_problem === "yes" && !this.health.organ_types.length && !this.health.organ_other) return "健康问卷：请选择或填写内脏器官问题";
      return "";
    },
    validateProfile() {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.profile.email && !emailPattern.test(this.profile.email)) return "学生档案：学生邮箱格式不正确";
      if (this.profile.parent_agent_email && !emailPattern.test(this.profile.parent_agent_email)) return "学生档案：父母或代理人邮箱格式不正确";
      if (this.profile.age !== "" && this.profile.age !== null && (!Number.isInteger(Number(this.profile.age)) || Number(this.profile.age) < 1 || Number(this.profile.age) > 99)) return "学生档案：年龄必须是1到99之间的整数";
      if (this.profile.photos.length > 20) return "学生档案：照片最多上传20张";
      return "";
    },
    normalizePhotos(photos) {
      return (photos || []).map(photo => {
        if (typeof photo === "string") return { url: photo };
        return {
          url: photo.url,
          object_key: photo.object_key || "",
          file_name: photo.file_name || photo.name || "",
          mime_type: photo.mime_type || "",
          file_size: Number(photo.file_size || 0),
          photo_type: photo.photo_type || "family",
        };
      });
    },
    buildPayload() {
      const payload = {
        union_id: this.unionId,
      };
      if (this.selectedDocumentKeys.includes("health")) payload.health = JSON.parse(JSON.stringify(this.health));
      if (this.selectedDocumentKeys.includes("profile")) {
        payload.profile = JSON.parse(JSON.stringify(this.profile));
        payload.profile.age = payload.profile.age === "" ? null : payload.profile.age;
        if (!this.unionId || this.photosDirty) payload.profile.photos = this.normalizePhotos(payload.profile.photos);
        else delete payload.profile.photos;
      }
      return payload;
    },
    errorMessage(error, fallback = "提交失败，请稍后重试") {
      return (error.response && error.response.data && error.response.data.msg) || error.message || fallback;
    },
    async submitDocuments() {
      if (this.photosUploading) {
        this.$message.warning("照片正在上传，请上传完成后再提交");
        return;
      }
      const healthError = this.selectedDocumentKeys.includes("health") ? this.validateHealth() : "";
      const profileError = this.selectedDocumentKeys.includes("profile") ? this.validateProfile() : "";
      const validationError = healthError || profileError;
      if (validationError) {
        this.$message.error(validationError);
        return;
      }
      this.submitting = true;
      try {
        if (this.selectedDocumentKeys.includes("health")) await this.collectHealthSignatures();
        const response = await SubmitDepartmentDocument(this.buildPayload());
        this.unionId = response.data.union_id || this.unionId;
        this.taskStatus = Number(response.data.status || 2);
        this.photosDirty = false;
        this.persistDraft(false);
        this.$message.success("部门文件提交成功");
      } catch (error) {
        this.$message.error(this.errorMessage(error));
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.department-page { min-height: 100vh; padding: 34px 18px 60px; background: #e9eef4; box-sizing: border-box; }
.page-shell { max-width: 1170px; margin: 0 auto; }
.page-toolbar { display: flex; align-items: flex-end; justify-content: space-between; gap: 28px; margin-bottom: 22px; }
.page-toolbar p { margin: 0; color: #718095; }
.toolbar-actions { display: flex; flex: 0 0 auto; gap: 10px; }
.page-state { display: grid; min-height: 360px; padding: 50px; place-content: center; border-radius: 8px; color: #718095; background: #fff; text-align: center; box-sizing: border-box; }
.page-state h2 { margin: 0 0 10px; color: #263b55; }
.page-state p { margin: 0 0 20px; }
.page-state-error { border-top: 4px solid #d45d5d; }
.document-paper { min-height: 720px; padding: 20px 48px 56px; border-radius: 3px; background: #fff; box-shadow: 0 14px 45px rgba(31, 48, 70, .12); }
.document-paper + .document-paper { margin-top: 24px; }
.page-footer { display: flex; align-items: center; justify-content: space-between; gap: 24px; margin-top: 18px; padding: 18px 22px; border-radius: 8px; color: #526174; background: #fff; }
.page-footer strong, .page-footer span { display: block; }
.page-footer span { margin-top: 4px; font-size: 13px; }
@media (max-width: 767px) {
  .department-page { padding: 14px 8px 36px; }
  .page-toolbar, .page-footer { align-items: stretch; flex-direction: column; }
  .toolbar-actions { display: grid; grid-template-columns: repeat(2, 1fr); }
  .document-paper { padding: 8px 14px 36px; }
}
@media print {
  .department-page { padding: 0; background: #fff; }
  .page-toolbar, .page-footer { display: none; }
  .document-paper { padding: 0; box-shadow: none; }
  .document-paper + .document-paper { margin-top: 0; break-before: page; page-break-before: always; }
}
</style>
